import * as React from "react";
import { Mutation } from "react-apollo";
import { Link } from "react-router-dom";
import * as moment from "moment";
import * as validator from "validator";
import { TiMail, TiUserOutline, TiLockClosedOutline } from "react-icons/ti";
import styled from "@emotion/styled";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupAddon
} from "reactstrap";

// GraphQL
import SignupMutation from "../../GraphQL/Mutations/Signup";

// Presenetation/UI
import LottieWrapper from "../../Components/Anim/LottieWrapper";
import { LogoText } from "../../Components/Text";
import { Colors } from "../../Themes";

interface IState {
  email: string;
  password: string;
  companyName: string;
  error: string | null;
  loading: boolean;
  view: string;
}

const SignUpContainer = styled.div`
  width: 100%;
  & input {
    background: transparent !important;
  }
  & .input-group {
    background: rgba(214, 219, 230, 0.36) !important;
    border-radius: 0.25rem;
  }
  & a {
    color: ${Colors.cmmsBlue};
    text-decoration: none;
  }
`;

const SuccessContainer = styled.div`
  width: 100%;
  position: relative;
  top: 90px;
`;

export default class LoginForm extends React.Component<{}, IState> {
  state: IState = {
    view: "form",
    email: "",
    password: "",
    companyName: "",
    loading: false,
    error: null
  };

  // Handle change
  handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const { name, value }: { name: string; value: string } = e.currentTarget;

    switch (name) {
      case "email":
        this.setState({ email: value });
        break;
      case "password":
        this.setState({ password: value });
        break;
      case "company":
        this.setState({ companyName: value });
        break;
      default:
        break;
    }
  };

  validateForm = (): boolean => {
    const { companyName, email, password } = this.state;
    // Check for undefined or empty infput fields
    if (!companyName || !password) {
      this.setError("Please fill in all fields");
      return false;
    }

    // Validate email
    if (!validator.isEmail(email)) {
      this.setError("Please enter a valid email address.");
      return false;
    }

    return true;
  };

  // Error
  setError = (error: string): void => {
    this.setState(
      {
        error
      },
      () => {
        setTimeout(() => {
          this.setState({ error: null });
        }, 3000);
      }
    );
  };

  // Return Loader
  renderLoader = () => {
    return (
      <SuccessContainer>
        <LottieWrapper
          loop={true}
          width={140}
          height={140}
          anim={require("../../LottieFiles/loading_circle.json")}
        />
        <br />
        <br />
        <p className="center-align">Please wait...</p>
      </SuccessContainer>
    );
  };

  // Render view
  renderView = () => {
    const { companyName, email, password, view } = this.state;
    if (view === "success") {
      return (
        <SuccessContainer>
          <LottieWrapper
            loop={false}
            width={320}
            height={280}
            anim={require("../../LottieFiles/success2.json")}
          />
          <br />
          <br />
          <p className="center-align">
            Please check your email to verify your account
          </p>
        </SuccessContainer>
      );
    }

    return (
      <div>
        <Link to="/">
          <LogoText>maintain.</LogoText>
        </Link>
        <br />
        <FormGroup>
          <Label for="userCompany">Company Name</Label>
          <InputGroup>
            <InputGroupAddon addonType="prepend">
              <TiUserOutline size="1.5em" />
            </InputGroupAddon>
            <Input
              type="text"
              name="company"
              value={companyName}
              id="userCompany"
              placeholder=""
              onChange={this.handleChange}
            />
          </InputGroup>
        </FormGroup>
        <FormGroup>
          <Label for="userEmail">Email Address</Label>
          <InputGroup>
            <InputGroupAddon addonType="prepend">
              <TiMail size="1.5em" />
            </InputGroupAddon>
            <Input
              type="email"
              name="email"
              value={email}
              id="userEmail"
              placeholder=""
              onChange={this.handleChange}
            />
          </InputGroup>
        </FormGroup>
        <FormGroup>
          <Label for="userPassword">Password</Label>
          <InputGroup>
            <InputGroupAddon addonType="prepend">
              <TiLockClosedOutline size="1.5em" />
            </InputGroupAddon>
            <Input
              type="password"
              name="password"
              value={password}
              id="userPassword"
              placeholder=""
              onChange={this.handleChange}
            />
          </InputGroup>
        </FormGroup>

        <FormGroup>
          By signing up, you are agreeing to our{" "}
          <Link to="#">Terms Of Service</Link> &amp;{" "}
          <Link to="#">Privacy Policy</Link>
        </FormGroup>
        <br />
        <Button>Sign Up</Button>
        <br />
        <br />
        <br />
      </div>
    );
  };

  render() {
    const { companyName, email, password, view } = this.state;

    return (
      <SignUpContainer>
        <Mutation<{}, {}> mutation={SignupMutation}>
          {(signUpUser, { loading, error }) => (
            <Form
              onSubmit={e => {
                e.preventDefault();
                this.validateForm();
                this.setState({ loading: true });

                signUpUser({
                  variables: {
                    email,
                    password,
                    companyName,
                    userRole: "ADMIN"
                  }
                })
                  .then(res => {
                    // console.log(res);
                    this.setState({ view: "success", loading: false });
                  })
                  .catch(err => {
                    // console.log(err);
                    this.setState({ loading: false });
                    this.setError(
                      "Oops, something went wrong. Please try again."
                    );
                  });
              }}
            >
              {loading && !this.state.error
                ? this.renderLoader()
                : this.renderView()}
              {this.state.error && (
                <p style={{ textAlign: "center", color: Colors.error }}>
                  {this.state.error}
                </p>
              )}
            </Form>
          )}
        </Mutation>
        {view === "success" || this.state.loading ? null : (
          <div>
            <FormGroup className="center-align">
              Already have an account? <Link to="/sign-in">Sign In</Link>
            </FormGroup>
            <FormGroup className="center-align">
              <span
                style={{
                  fontSize: "12px",
                  color: Colors.semiBlack,
                  marginTop: 30
                }}
              >
                Copyright &copy; {moment().format("YYYY")}
              </span>
            </FormGroup>
          </div>
        )}
      </SignUpContainer>
    );
  }
}



// WEBPACK FOOTER //
// ./src/Containers/SignUp/SignUpForm.tsx